import { GET_AUCTION_FORMS } from "core/apollo/graphql";
import { SEARCH_ACTION_GENERATE_FORM } from "core/consts";
import { useApolloEncryptionContext } from "core/model/patients/encryption/utils";
import { getErrorMessage } from "core/model/utils/errors";
import {
  Auction,
  DocsFormName,
  DocsFormStatusResponse,
  FileState,
  ValueOf,
} from "core/types";
import useSearchAction from "dsl/hooks/useSearchAction";
import { useCallback } from "react";
import { convertEncryptedFieldOut } from "react-forms-state";
import { DocsFormDataMapped } from "..";
import { SourceInformation } from "../../useDocsForm";
import { FORM_NAME_TO_FILE_TYPE, MigratedForms } from "../../utils";

const FIELD_CREATION_TYPE = {
  INFERENCE: "inference",
  USER: "user",
} as const;

type FieldCreationType = ValueOf<typeof FIELD_CREATION_TYPE>;

export type FieldMetadata = {
  manually_edited: boolean;
  creation_type: FieldCreationType;
  source_documents: { file_id: FileState["id"] }[];
};

export type FormMetadata<T extends DocsFormName> = {
  [key in keyof DocsFormDataMapped<T>]: FieldMetadata;
};

export type DocsSubmittedFormData<T extends DocsFormName> = {
  metadata: FormMetadata<T>;
  values: DocsFormDataMapped<T>;
};

export const useGetDischargeLetterOnSubmit = <T extends DocsFormName>({
  auction,
  formData: previousFormData,
}: {
  auction: Auction;
  formData?: DocsFormDataMapped<T> | null | undefined;
}) => {
  const encryptionContext = useApolloEncryptionContext({ decrypt: true });
  const [generateForm, generateFormProgress, resetProgress] = useSearchAction({
    actionType: SEARCH_ACTION_GENERATE_FORM,
    refetchQueries: [
      {
        context: { encryptionContext },
        query: GET_AUCTION_FORMS,
        variables: { auctionId: auction.id },
      },
    ],
  });

  const onSubmit = useCallback(
    async (
      formData: DocsFormDataMapped<typeof formName>,
      formName: MigratedForms,
      sourceInformation: SourceInformation<typeof formName>,
      docsFillFormResult: DocsFormStatusResponse | null,
    ) => {
      const metadata: FormMetadata<typeof formName> = Object.entries(
        formData,
      ).reduce(
        (acc, [key, value]) => {
          const inferredValue = docsFillFormResult?.form[key]?.value;
          const metadataForField = sourceInformation.getFieldMetadata(
            key as keyof DocsFormDataMapped<typeof formName>,
          );
          const sourceDocuments = metadataForField?.source_documents ?? [];
          const previousFieldValue =
            previousFormData?.[key as keyof DocsFormDataMapped<T>];

          return {
            ...acc,
            [key]: {
              creation_type:
                inferredValue != null && value != null
                  ? FIELD_CREATION_TYPE.INFERENCE
                  : metadataForField?.creation_type ?? FIELD_CREATION_TYPE.USER,
              manually_edited:
                // if theres an inferred value, field is edited if the value submitted is different from the inferred value
                inferredValue != null
                  ? value !== inferredValue
                  : // is the value wasnt inferred, and there was a previous value, check if the prvious value is different from the submitted value
                  previousFieldValue != null
                  ? previousFieldValue !== value ||
                    metadataForField?.manually_edited
                  : // if the value wasnt inferred, and there was no previous value,
                    false,
              source_documents: sourceDocuments,
            },
          };
        },
        {} as FormMetadata<typeof formName>,
      );

      const submittedFormData: DocsSubmittedFormData<typeof formName> = {
        metadata,
        values: formData,
      };

      const updatedAuction = {
        ...auction,
        forms: [
          {
            form_data: convertEncryptedFieldOut(
              JSON.stringify(submittedFormData),
            ),
          },
        ],
      } as Auction;

      await generateForm({
        auction,
        context: {
          auction: updatedAuction,
          form_type: FORM_NAME_TO_FILE_TYPE[formName],
        },
        onCompleted: () => {
          resetProgress();
        },
        onError: (err) => {
          console.error(`error saving form - ${getErrorMessage(err)}`);
        },
      });
    },
    [
      convertEncryptedFieldOut,
      generateForm,
      auction,
      previousFormData,
      encryptionContext,
    ],
  );

  return { onSubmit, onSubmitProgress: generateFormProgress };
};
